import React from "react";
import { onGroupMemberAdded, onGroupMemberRemoved, onUserFollowAdded, onUserFollowRemoved } from "@/main/common/EventRegistry";

/**
 * FI-631: per-scope counters of follow and group-membership changes, carried in the `key` and
 * `restorationKey` of the profile and dashboard lists those changes reshape (profile Groups, Followers,
 * Following; dashboard Groups and Followers/Following), so a Back-restoration can never re-seed pages
 * that predate the change.
 *
 * <p><b>Why module-level, not component state.</b> The same reason as the group post list's
 * `groupDiscussionsMutationKey` (QC-14 / QoL `1j`), of which this is the generalisation. The restoration
 * cache (`NavigationListStateCache`) is module memory keyed by `(navigationId, restorationKey)` and keeps
 * the pre-change entry after the key moves on, and Back REMOUNTS the page under the same `navigationId`.
 * A counter in React state resets to 0 on that remount and reads the stale entry again; it also never
 * sees a change made while the page is away (leaving a group on the group page, then Back). This counter
 * lives exactly as long as the cache (the tab's JS context) and counts from the first list that uses it.
 *
 * <p>Not a `useSyncExternalStore` store: one integer per scope, read at mount and pushed to the mounted
 * list through a plain subscription, so no chrome subscribes to it (rule 31 does not come into play).
 */
export type ViewerMutationScope =
    /** Someone followed or unfollowed this username. */
    | `followers:${string}`
    /** This username followed or unfollowed someone. */
    | `following:${string}`
    /** This username joined or left (or was removed from) a group. */
    | `group-membership:${string}`;

const counters = new Map<string, number>();
const listeners = new Set<(scope: string) => void>();
let subscribed = false;

function bump(scope: ViewerMutationScope | null) {
    if (!scope) {
        return;
    }
    counters.set(scope, (counters.get(scope) ?? 0) + 1);
    listeners.forEach((listener) => listener(scope));
}

function scopeFor(prefix: "followers" | "following" | "group-membership", username: string | undefined | null): ViewerMutationScope | null {
    return username ? `${prefix}:${username}` : null;
}

/** Idempotent: the first list to mount starts counting, and it never stops for the tab's life. */
export function ensureViewerMutationTracking(): void {
    if (subscribed) {
        return;
    }
    subscribed = true;
    const onFollowChange = (payload: { targetUsername?: string; actorUsername?: string } | undefined) => {
        bump(scopeFor("followers", payload?.targetUsername));
        bump(scopeFor("following", payload?.actorUsername));
    };
    onUserFollowAdded(onFollowChange);
    onUserFollowRemoved(onFollowChange);
    onGroupMemberAdded((payload) => bump(scopeFor("group-membership", payload?.member?.username)));
    onGroupMemberRemoved((payload) => bump(scopeFor("group-membership", payload?.username)));
}

export function readViewerMutationKey(scope: ViewerMutationScope | null): number {
    return scope ? (counters.get(scope) ?? 0) : 0;
}

/** The current counter for `scope`, re-rendering the caller when it moves. `null` is a constant 0. */
export function useViewerMutationKey(scope: ViewerMutationScope | null): number {
    ensureViewerMutationTracking();
    const [value, setValue] = React.useState(() => readViewerMutationKey(scope));

    React.useEffect(() => {
        // Re-read at subscribe time: a change between render and effect must not be missed.
        setValue(readViewerMutationKey(scope));
        if (!scope) {
            return;
        }
        const listener = (changed: string) => {
            if (changed === scope) {
                setValue(readViewerMutationKey(scope));
            }
        };
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    }, [scope]);

    return value;
}
